import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { MilestoneProgressBar } from "../../../components/UIToolkit/Graphs/MilestoneProgressBar/MilestoneProgressBar";
import { AccentIconTile } from "../../../components/UIToolkit/molecules/AccentIconTile";
import { SystemBanner } from "../../../components/UIToolkit/molecules/SystemBanner";
import { ICON_SIZE, RADIUS } from "../../../theme/tokens";
import type { PbjPlanActionProgress, PbjPlanValue } from "../../../utils/models";
import { PBJ_ALPHA } from "../PredictionTab/colors";
import { RECOMMENDATION_TYPES, recColor } from "../PredictionTab/recommendations.constants";
import { PlanActionContextChips } from "./PlanActionContextChips";
import { PlanActionFootnotes } from "./PlanActionFootnotes";
import { PlanActionRateBox } from "./PlanActionRateBox";
import { PlanActionStatusLine } from "./PlanActionStatusLine";
import { behindBannerCopy } from "./planActionCopy.utils";
import {
	type ActionFormat,
	buildActionMilestones,
	formatPlanValueInDirection,
	formatWholeNumber,
	isActionJudged,
	movedBackwards,
	type PlanBasis,
} from "./planValue.utils";

type Props = {
	action: PbjPlanActionProgress;
	basis: PlanBasis;
	weeksRemaining: number;
};

export const PlanActionCard: FC<Props> = ({ action, basis, weeksRemaining }) => {
	const { t } = useTranslation();
	const rec = RECOMMENDATION_TYPES.find((r) => r.id === action.id);
	const theme = useTheme();
	// Levers carry a theme-derived `colorKey`, not a baked hex, so the tile re-tints with the org's brand.
	const accent = rec ? recColor(theme, rec.colorKey) : undefined;
	const format: ActionFormat = { basis, unit: t(basis === "hours" ? "pbj_model.tracker.unit_hours" : "pbj_model.tracker.unit_hppd") };
	// Every figure on a cut card is signed negative while the cut is working. Each one is printed IN
	// THE LEVER'S DIRECTION (`directionSign`), so "40 hrs cut" reads positive and a cut that staffing
	// has pushed the wrong way reads negative — never `Math.abs`, which would show going backwards as
	// progress. For an add, every figure below is exactly what it was.
	const isCut = action.direction === "cut";
	const inDirection = (value: PbjPlanValue): string => `${formatPlanValueInDirection(value, basis, action.direction)} ${format.unit}`;
	const backwards = movedBackwards(action, basis);
	// Both sides of the on-track comparison are charged over completed weeks, so before the first one
	// closes the verdict is vacuously true and is withheld rather than shown green. `behind` follows the
	// same gate: a lever that cannot be judged yet has not been found wanting either.
	const judged = isActionJudged(action.days);
	const behind = action.feasible && judged && !action.onTrack;
	const behindCopy = behind ? behindBannerCopy(action, format, t("pbj_model.tracker.per_week_suffix")) : null;

	const milestones = buildActionMilestones(action, format, t);
	// A band edge costing more than the whole commitment can't be reached by this plan. Saying so
	// beats silently dropping it off the right end of the bar.
	const beyondPlan = milestones.filter((m) => m.pct > 100).length;

	// Where the pace set so far lands at quarter end. Omitted outright when the engine has no pace to
	// extend — a plan saved today, or a weekend lever before its first weekend — rather than drawn at
	// zero, which would forecast total failure on a quarter nobody has had the chance to work.
	const projection = action.projected
		? {
				pct: action.projected.pct,
				label: t("pbj_model.tracker.projected_on_bar", { value: inDirection(action.projected.delivered) }),
				reachesTarget: action.projected.meetsGoal,
				tooltip: t("pbj_model.tracker.projection_tooltip"),
			}
		: undefined;

	return (
		<Paper variant="outlined" sx={{ p: 2.5, borderRadius: `${RADIUS.POPOVER}px` }} data-testid={`pbj-model-plan-action-${action.id}`}>
			<Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ justifyContent: "space-between", alignItems: { md: "center" }, mb: 2 }}>
				<Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
					{rec && accent && (
						<AccentIconTile size="xs" color={accent} tint={alpha(accent, PBJ_ALPHA.TINT)}>
							{rec.icon}
						</AccentIconTile>
					)}
					<Box sx={{ minWidth: 0 }}>
						<Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
							{rec ? t(rec.titleKey) : action.id}
						</Typography>
						<PlanActionStatusLine action={action} judged={judged} deliveredText={inDirection(action.delivered)} />
						<PlanActionContextChips action={action} />
					</Box>
				</Stack>

				<PlanActionRateBox action={action} format={format} behind={behind} />
			</Stack>

			{/* Why the lever reads behind, in the plan's own numbers, with the way back. */}
			{behindCopy && (
				<SystemBanner
					tone="error"
					icon={<ErrorOutlineIcon />}
					title={t("pbj_model.tracker.behind_title")}
					description={t(behindCopy.key, behindCopy.values)}
					sx={{ mb: 2 }}
					dataTestId={`pbj-model-plan-action-behind-${action.id}`}
				/>
			)}

			{/* The bar fills to `deliveredToDate` (every elapsed day), NOT to `delivered`. The value
			    label rides the end of the fill so progress reads against the coloured line itself. */}
			<MilestoneProgressBar
				value={action.progressPct}
				milestones={milestones}
				projection={projection}
				valueLabel={t(isCut ? "pbj_model.tracker.cut_on_bar" : "pbj_model.tracker.delivered_on_bar", { value: inDirection(action.deliveredToDate) })}
				tone={action.feasible ? (action.onTrack ? "primary" : "warning") : "warning"}
				ariaLabel={t("pbj_model.tracker.action_progress_aria", { action: rec ? t(rec.titleKey) : action.id })}
				dataTestId={`pbj-model-plan-action-bar-${action.id}`}
			/>
			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 1 }}>
				<Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
					{t("pbj_model.tracker.baseline_label")}
				</Typography>
				<Typography variant="caption" sx={{ fontWeight: 700 }} data-testid={`pbj-model-plan-action-pct-${action.id}`}>
					{t("pbj_model.tracker.percent_complete", { value: formatWholeNumber(action.progressPct) })}
				</Typography>
				{/* The commitment, bottom-right — the number the whole bar is filling toward. */}
				<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "text.primary" }}>
					<TrackChangesIcon sx={{ fontSize: ICON_SIZE.PILL }} />
					<Typography variant="caption" sx={{ fontWeight: 700 }} data-testid={`pbj-model-plan-action-goal-total-${action.id}`}>
						{t(isCut ? "pbj_model.tracker.goal_cut_on_bar" : "pbj_model.tracker.goal_on_bar", { value: inDirection(action.goalTotal) })}
					</Typography>
				</Stack>
			</Stack>
			<PlanActionFootnotes action={action} format={format} weeksRemaining={weeksRemaining} beyondPlan={beyondPlan} backwards={backwards} />
		</Paper>
	);
};
